import SearchableSelect from '@/components/SearchableSelect';
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from '@/components/Layout/PageHeader';
import { soCreateAPI, billingOpsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ItemSearchSelect } from '@/components/ItemSearchSelect';
import { toast } from 'sonner';
import {
  Plus, Trash, MagnifyingGlass, FloppyDisk, ArrowRight, Receipt,
} from '@phosphor-icons/react';

const VISIT_TYPES = ['Clinic Visit', 'Home Visit'];
import { INVOICE_TYPES, coreFieldsError } from '@/lib/mandatoryFields';

export default function CreateInvoicePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const soIdParam = searchParams.get('so_id');

  const [branches, setBranches] = useState([]);
  const [audiologists, setAudiologists] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [catalogItems, setCatalogItems] = useState([]);
  const [invNumber, setInvNumber] = useState('');
  const [invNumberManual, setInvNumberManual] = useState('');
  const [useManualInv, setUseManualInv] = useState(false);
  const [saving, setSaving] = useState(false);

  // Convert SO mode
  const [convertMode, setConvertMode] = useState(!!soIdParam);
  const [soSearch, setSoSearch] = useState('');
  const [convertibleSOs, setConvertibleSOs] = useState([]);
  const [showSODropdown, setShowSODropdown] = useState(false);
  const [selectedSO, setSelectedSO] = useState(null);

  const [form, setForm] = useState({
    customer_id: '', customer_name: '', customer_phone: '', customer_state: '', customer_gstin: '',
    branch_id: '', invoice_date: new Date().toISOString().split('T')[0],
    due_date: '', audiologist_name: '', audiologist_employee_number: '',
    second_audiologist_name: '', second_audiologist_employee_number: '',
    lead_source: '', visit_type: '', invoice_type: '', customer_notes: '', terms_and_conditions: '',
    so_id: soIdParam || '',
  });

  const [lineItems, setLineItems] = useState([
    { item_id: '', name: '', product_code: '', hsn_sac: '', quantity: 1, rate: 0, discount_percent: 0, discount_amount: 0, tax_percent: 0, serial_numbers: [], _discountType: 'percent', _serialInput: '', _availableSerials: [], _showSerialDropdown: false }
  ]);

  const [custSearch, setCustSearch] = useState('');
  const [showCustDropdown, setShowCustDropdown] = useState(false);

  // Close serial dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest('[data-testid^="inv-serial-"]') && !e.target.closest('[data-testid^="inv-serial-btn-"]') && !e.target.closest('[data-testid^="inv-serial-dropdown-"]')) {
        setLineItems(prev => prev.map(li => ({ ...li, _showSerialDropdown: false })));
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [brRes, audRes, lsRes, itemsRes] = await Promise.allSettled([
          soCreateAPI.branches(),
          soCreateAPI.audiologists(),
          soCreateAPI.leadSources(),
          soCreateAPI.items({ per_page: 2000 }),
        ]);
        if (brRes.status === 'fulfilled') setBranches(brRes.value.data.branches || []);
        if (audRes.status === 'fulfilled') setAudiologists(audRes.value.data.audiologists || []);
        if (lsRes.status === 'fulfilled') setLeadSources(lsRes.value.data.lead_sources || []);
        if (itemsRes.status === 'fulfilled') setCatalogItems(itemsRes.value.data.items || []);
      } catch (e) { console.error(e); }
    };
    loadData();
  }, []);

  // Load SO data if so_id param (wait for branches so we can resolve the branch + serials)
  useEffect(() => {
    if (!soIdParam || branches.length === 0) return;
    (async () => {
      try {
        const { data } = await billingOpsAPI.convertibleSOs({ search: soIdParam });
        const so = (data.sales_orders || []).find(s => s.id === soIdParam);
        if (so) prefillFromSO(so);
      } catch {}
    })();
  }, [soIdParam, branches]);

  // Fetch next invoice number when branch changes
  useEffect(() => {
    if (!form.branch_id || useManualInv) return;
    const branch = branches.find(b => b.id === form.branch_id);
    const sc = branch?.billing_state_code || branch?.state_code;
    if (!sc) return;
    soCreateAPI.nextInvNumber(sc)
      .then(r => setInvNumber(r.data.next_inv_number))
      .catch(() => {});
  }, [form.branch_id, branches, useManualInv]);

  // Customer search
  useEffect(() => {
    if (custSearch.length < 2) { setCustomers([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await soCreateAPI.customers({ search: custSearch, per_page: 10 });
        setCustomers(data.customers || []);
        setShowCustDropdown(true);
      } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [custSearch]);

  // SO search for conversion — auto-load when branch selected, also re-run on typing
  useEffect(() => {
    if (!convertMode) { setConvertibleSOs([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await billingOpsAPI.convertibleSOs({ search: soSearch, branch_id: form.branch_id });
        setConvertibleSOs(data.sales_orders || []);
        if (document.activeElement?.getAttribute('data-testid') === 'inv-so-search') setShowSODropdown(true);
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
  }, [soSearch, convertMode, form.branch_id]);

  const prefillFromSO = async (so) => {
    setSelectedSO(so);
    // Resolve the branch the SO belongs to — prefer a real match in the branches list,
    // falling back to a name match (clinic-created SOs often have an empty clinic_id). (#6)
    const nm = (so.clinic_name || so.location_name || '').trim().toLowerCase();
    const matched = branches.find(b => b.id === so.clinic_id)
      || branches.find(b => b.id === so.branch_id)
      || branches.find(b => b.id === so.location_id)
      || (nm ? branches.find(b => (b.name || '').trim().toLowerCase() === nm) : null);
    const resolvedBranchId = matched?.id || so.clinic_id || so.branch_id || '';
    const branchName = matched?.name || so.clinic_name || so.location_name || '';
    setForm(f => ({
      ...f,
      so_id: so.id,
      customer_id: so.customer_id || '',
      customer_name: so.customer_name || so.customer?.name || '',
      customer_phone: so.customer_phone || so.customer?.phone || '',
      customer_state: so.customer_state || so.place_of_supply || '',
      branch_id: resolvedBranchId || f.branch_id,
      audiologist_name: so.audiologist?.name || so.audiologist_name || '',
      audiologist_employee_number: so.audiologist?.employee_number || so.audiologist_employee_number || '',
      lead_source: (typeof so.lead_source === 'object' ? so.lead_source?.name : so.lead_source) || '',
      visit_type: so.visit_type || '',
      invoice_type: so.invoice_type || f.invoice_type || '',
    }));
    setCustSearch(so.customer_name || so.customer?.name || '');
    setSoSearch(so.so_number || '');
    const soItems = so.line_items || so.items || [];
    if (soItems.length === 0) return;
    const baseItems = soItems.map(li => {
      const cat = catalogItems.find(c => c.id === (li.item_id || ''));
      return {
      item_id: li.item_id || '', name: li.name || li.item_name || '',
      product_code: li.product_code || '', hsn_sac: li.hsn_sac || '',
      quantity: li.quantity || 1, rate: li.rate || li.price || 0,
      discount_percent: li.discount_percent || 0, discount_amount: li.discount_amount || 0,
      tax_percent: li.tax_percent || 0, serial_numbers: li.serial_numbers || [],
      _discountType: li.discount_amount > 0 ? 'amount' : 'percent',
      _serialInput: (li.serial_numbers || []).join(', '),
      _availableSerials: [], _showSerialDropdown: false,
      _requiresSerial: cat ? (!!cat.track_inventory && !!cat.track_serial_number && String(cat.item_type || '').toLowerCase() !== 'service') : false,
      };
    });
    setLineItems(baseItems);
    // Fetch available stock serials for each line at the resolved branch so they can be
    // picked at conversion time (previously the dropdown showed "No stock serials"). (#7)
    if (branchName) {
      const withSerials = await Promise.all(baseItems.map(async (li) => {
        if (!li.item_id && !li.name) return li;
        try {
          const { data } = await soCreateAPI.availableSerials({
            item_id: li.item_id || '', item_name: li.name || '', location: branchName,
          });
          return { ...li, _availableSerials: data.serial_numbers || [] };
        } catch { return li; }
      }));
      setLineItems(withSerials);
    }
  };

  const selectCustomer = (c) => {
    setForm(f => ({
      ...f, customer_id: c.id,
      customer_name: c.display_name || `${c.first_name} ${c.last_name}`.trim(),
      customer_phone: c.phone || '', customer_state: c.place_of_supply || '', customer_gstin: c.gstin || c.gst_no || '',
    }));
    setCustSearch(c.display_name || c.first_name);
    setShowCustDropdown(false);
  };

  const selectCatalogItem = async (idx, itemId) => {
    const item = catalogItems.find(i => i.id === itemId);
    if (!item) return;
    const gstRate = item.tax_preference === 'taxable' ? parseFloat(item.intra_state_gst || '18') : 0;
    setLineItems(prev => prev.map((li, i) => i === idx ? {
      ...li, item_id: item.id, name: item.name, product_code: item.product_code || '',
      hsn_sac: item.hsn_sac_code || '', rate: item.mrp || 0, tax_percent: gstRate,
      serial_numbers: [], _serialInput: '', _availableSerials: [], _showSerialDropdown: false,
      _requiresSerial: !!item.track_inventory && !!item.track_serial_number && String(item.item_type || '').toLowerCase() !== 'service',
    } : li));

    // Auto-fetch available serial numbers for this item + branch
    if (form.branch_id && item.id) {
      try {
        const branch = branches.find(b => b.id === form.branch_id);
        const locationName = branch?.name || '';
        const { data } = await soCreateAPI.availableSerials({
          item_id: item.id,
          item_name: item.name,
          location: locationName,
        });
        if (data.serial_numbers && data.serial_numbers.length > 0) {
          setLineItems(prev => prev.map((li, i) => i === idx ? {
            ...li, _availableSerials: data.serial_numbers,
          } : li));
        }
      } catch (e) {
        console.log('No serials found for item + location');
      }
    }
  };

  const updateLineItem = (idx, field, value) => {
    setLineItems(prev => prev.map((li, i) => i === idx ? { ...li, [field]: value } : li));
  };

  const toggleSerial = (idx, sn) => {
    setLineItems(prev => prev.map((li, i) => {
      if (i !== idx) return li;
      const selected = li.serial_numbers || [];
      const isSelected = selected.includes(sn);
      const updated = isSelected ? selected.filter(s => s !== sn) : [...selected, sn];
      return { ...li, serial_numbers: updated, _serialInput: updated.join(', ') };
    }));
  };

  const addLineItem = () => {
    setLineItems(prev => [...prev, { item_id: '', name: '', product_code: '', hsn_sac: '', quantity: 1, rate: 0, discount_percent: 0, discount_amount: 0, tax_percent: 0, serial_numbers: [], _discountType: 'percent', _serialInput: '', _availableSerials: [], _showSerialDropdown: false }]);
  };

  const removeLineItem = (idx) => {
    if (lineItems.length <= 1) return;
    setLineItems(prev => prev.filter((_, i) => i !== idx));
  };

  const calcLine = (li) => {
    const lineAmt = li.quantity * li.rate;
    const disc = li._discountType === 'amount' ? (parseFloat(li.discount_amount) || 0) : lineAmt * ((parseFloat(li.discount_percent) || 0) / 100);
    const taxable = lineAmt - disc;
    const tax = taxable * ((parseFloat(li.tax_percent) || 0) / 100);
    return { lineAmt, disc, taxable, tax, total: taxable + tax };
  };

  const totals = lineItems.reduce((acc, li) => {
    const c = calcLine(li);
    return { subtotal: acc.subtotal + c.lineAmt, discount: acc.discount + c.disc, tax: acc.tax + c.tax, grand: acc.grand + c.total };
  }, { subtotal: 0, discount: 0, tax: 0, grand: 0 });

  const fmt = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const handleSubmit = async () => {
    if (!form.branch_id) { toast.error('Branch/Location is required'); return; }
    const coreErr = coreFieldsError({ name: form.customer_name || custSearch, phone: form.customer_phone, leadSource: form.lead_source,
      audiologist: form.audiologist_employee_number || form.audiologist_name, invoiceType: form.invoice_type });
    if (coreErr) { toast.error(coreErr); return; }
    if (!lineItems.some(li => li.name)) { toast.error('At least one item is required'); return; }

    // Serial number is mandatory for inventory-tracked goods (track_inventory ticked)
    const missingSerial = lineItems.find(li => {
      if (!li.name || !li._requiresSerial) return false;
      const qty = parseFloat(li.quantity) || 1;
      return (li.serial_numbers || []).length < qty;
    });
    if (missingSerial) {
      toast.error(`Serial number is required for "${missingSerial.name}" (needs ${parseFloat(missingSerial.quantity) || 1}).`);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        customer_name: form.customer_name || custSearch,
        invoice_number_manual: useManualInv ? invNumberManual : '',
        line_items: lineItems.filter(li => li.name).map(li => ({
          item_id: li.item_id, name: li.name, product_code: li.product_code,
          hsn_sac: li.hsn_sac, quantity: parseFloat(li.quantity) || 1,
          rate: parseFloat(li.rate) || 0,
          discount_percent: li._discountType === 'percent' ? (parseFloat(li.discount_percent) || 0) : 0,
          discount_amount: li._discountType === 'amount' ? (parseFloat(li.discount_amount) || 0) : 0,
          tax_percent: parseFloat(li.tax_percent) || 0,
          serial_numbers: li._serialInput ? li._serialInput.split(',').map(s => s.trim()).filter(Boolean) : [],
        })),
      };
      const { data } = await billingOpsAPI.createInvoice(payload);
      toast.success(`Invoice ${data.invoice_number} created successfully!`);
      navigate('/invoices');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create invoice');
    } finally { setSaving(false); }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="create-invoice-page">
      <PageHeader title="Create Tax Invoice" subtitle="Generate a new tax invoice or convert from Sales Order" />

      <div className="mt-6 space-y-6">
        {/* Mode Toggle */}
        <div className="flex gap-3">
          <Button variant={convertMode ? 'outline' : 'default'} size="sm"
            className={!convertMode ? 'bg-[#0F4C81] text-white' : ''}
            onClick={() => { setConvertMode(false); setSelectedSO(null); setForm(f => ({ ...f, so_id: '' })); }}
            data-testid="inv-mode-standalone">
            <Receipt size={14} className="mr-1" /> Standalone Invoice
          </Button>
          <Button variant={convertMode ? 'default' : 'outline'} size="sm"
            className={convertMode ? 'bg-[#0F4C81] text-white' : ''}
            onClick={() => setConvertMode(true)}
            data-testid="inv-mode-convert">
            <ArrowRight size={14} className="mr-1" /> Convert from SO
          </Button>
        </div>

        {/* SO Conversion Picker */}
        {convertMode && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs text-amber-800 font-medium">Search Sales Order to Convert</label>
              {form.branch_id ? (
                <span className="text-[11px] text-amber-700">{convertibleSOs.length} pending SO(s) for this branch</span>
              ) : (
                <span className="text-[11px] text-amber-700 font-medium">Select a branch below to see pending SOs</span>
              )}
            </div>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-500" />
              <Input
                value={soSearch} onChange={e => setSoSearch(e.target.value)}
                placeholder={form.branch_id ? "Search by SO number or customer name... (leave blank to see all)" : "Select branch first"}
                className="pl-9 border-amber-300"
                onFocus={() => convertibleSOs.length > 0 && setShowSODropdown(true)}
                onBlur={() => setTimeout(() => setShowSODropdown(false), 200)}
                disabled={!form.branch_id}
                data-testid="inv-so-search"
              />
              {showSODropdown && convertibleSOs.length > 0 && (
                <div className="absolute z-50 top-full left-0 right-0 bg-white border border-amber-200 rounded-lg mt-1 shadow-lg max-h-64 overflow-y-auto">
                  {convertibleSOs.map(so => (
                    <button key={so.id} onMouseDown={() => { prefillFromSO(so); setShowSODropdown(false); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-amber-50 flex justify-between items-center border-b border-amber-100 last:border-0" data-testid={`so-pick-${so.id}`}>
                      <div>
                        <div className="font-medium text-[#0B1B3D]">{so.so_number}</div>
                        <div className="text-[11px] text-[#64748B]">{so.customer_name || so.customer?.name || '—'} • {so.date || ''}</div>
                      </div>
                      <span className="text-xs font-semibold text-[#0F4C81]">{fmt(so.grand_total || so.total)}</span>
                    </button>
                  ))}
                </div>
              )}
              {form.branch_id && convertibleSOs.length === 0 && soSearch && (
                <p className="text-[11px] text-amber-700 mt-1">No pending SOs matching "{soSearch}"</p>
              )}
            </div>
            {selectedSO && (
              <p className="text-xs text-amber-700 mt-2">Converting: <strong>{selectedSO.so_number}</strong> - {selectedSO.customer_name || selectedSO.customer?.name} ({fmt(selectedSO.grand_total)})</p>
            )}
          </div>
        )}

        {/* Invoice Number & Branch */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-5 space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Branch/Location *</label>
              <SearchableSelect value={form.branch_id} onChange={v => setForm(f => ({ ...f, branch_id: v }))} placeholder="Select branch..." testid="inv-branch" buttonClassName="w-full"
                  options={[...branches.map(b => ({ value: b.id, label: `${b.name} (${b.billing_state_code || b.state_code})` , keywords: b.code || b.clinic_code || "" }))]} />
            </div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">
                Invoice #
                <button className="ml-2 text-[10px] text-blue-600 underline" onClick={() => setUseManualInv(!useManualInv)}>
                  {useManualInv ? 'Auto-generate' : 'Manual entry'}
                </button>
              </label>
              {useManualInv ? (
                <Input value={invNumberManual} onChange={e => setInvNumberManual(e.target.value)} placeholder="INV-DL-2627-XXXX" data-testid="inv-number-manual" />
              ) : (
                <Input value={invNumber} disabled className="bg-gray-50" data-testid="inv-number-auto" />
              )}
            </div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Invoice Date</label>
              <Input type="date" value={form.invoice_date} onChange={e => setForm(f => ({ ...f, invoice_date: e.target.value }))} data-testid="inv-date" />
            </div>
          </div>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Due Date</label>
              <Input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} data-testid="inv-due-date" />
            </div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Invoice Type <span className="text-red-500">*</span></label>
              <SearchableSelect value={form.invoice_type} onChange={v => setForm(f => ({ ...f, invoice_type: v }))} placeholder="Select..." testid="inv-type" buttonClassName="w-full"
                  options={[...INVOICE_TYPES.map(t => ({ value: t, label: t }))]} />
            </div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Lead Source <span className="text-red-500">*</span></label>
              <Select value={form.lead_source} onValueChange={v => setForm(f => ({ ...f, lead_source: v }))}>
                <SelectTrigger data-testid="inv-lead-source"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  {leadSources.map(ls => <SelectItem key={ls.id} value={ls.name}>{ls.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Visit Type</label>
              <SearchableSelect value={form.visit_type} onChange={v => setForm(f => ({ ...f, visit_type: v }))} placeholder="Select..." testid="inv-visit-type" buttonClassName="w-full"
                  options={[...VISIT_TYPES.map(v => ({ value: v, label: v }))]} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Audiologist <span className="text-red-500">*</span></label>
              <Select value={form.audiologist_employee_number || (form.audiologist_name ? `name:${form.audiologist_name}` : '')} onValueChange={empNum => {
                if (empNum.startsWith('name:')) return;
                const aud = audiologists.find(a => a.employee_number === empNum);
                setForm(f => ({ ...f, audiologist_employee_number: empNum, audiologist_name: aud?.full_name || '' }));
              }}>
                <SelectTrigger data-testid="inv-audiologist"><SelectValue placeholder="Select audiologist..." /></SelectTrigger>
                <SelectContent>
                  {!form.audiologist_employee_number && form.audiologist_name && (
                    <SelectItem value={`name:${form.audiologist_name}`}>{form.audiologist_name} (from SO)</SelectItem>
                  )}
                  {audiologists.map(a => <SelectItem key={a.employee_number} value={a.employee_number}>{a.full_name} - {a.location}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Second Audiologist (Optional)</label>
              <Select value={form.second_audiologist_employee_number} onValueChange={empNum => {
                const aud = audiologists.find(a => a.employee_number === empNum);
                setForm(f => ({ ...f, second_audiologist_employee_number: empNum, second_audiologist_name: aud?.full_name || '' }));
              }}>
                <SelectTrigger data-testid="inv-second-audiologist"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  {audiologists.map(a => <SelectItem key={a.employee_number} value={a.employee_number}>{a.full_name} - {a.location}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Customer */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-5 space-y-4">
          <h3 className="text-sm font-semibold text-[#0F172A]">Customer Details</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="relative col-span-2">
              <label className="text-xs text-[#64748B] block mb-1">Customer Name *</label>
              <div className="relative">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  value={custSearch}
                  onChange={e => { setCustSearch(e.target.value); setForm(f => ({ ...f, customer_name: e.target.value, customer_id: '' })); }}
                  placeholder="Search or type customer name..."
                  className="pl-9"
                  onFocus={() => customers.length > 0 && setShowCustDropdown(true)}
                  onBlur={() => setTimeout(() => setShowCustDropdown(false), 200)}
                  data-testid="inv-customer-name"
                />
                {showCustDropdown && customers.length > 0 && (
                  <div className="absolute z-50 top-full left-0 right-0 bg-white border border-[#E2E8F0] rounded-lg mt-1 shadow-lg max-h-48 overflow-y-auto">
                    {customers.map(c => (
                      <button key={c.id} onMouseDown={() => selectCustomer(c)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-[#F8FAFC] flex justify-between" data-testid={`inv-cust-option-${c.id}`}>
                        <span className="font-medium">{c.display_name}</span>
                        <span className="text-xs text-[#64748B]">{c.phone}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Phone <span className="text-red-500">*</span></label>
              <Input value={form.customer_phone} onChange={e => setForm(f => ({ ...f, customer_phone: e.target.value }))} placeholder="+91..." data-testid="inv-customer-phone" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Place of Supply (State)</label>
              <Input value={form.customer_state} onChange={e => setForm(f => ({ ...f, customer_state: e.target.value }))} placeholder="e.g. Delhi" data-testid="inv-customer-state" />
            </div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Customer GSTIN (B2B)</label>
              <Input value={form.customer_gstin} onChange={e => setForm(f => ({ ...f, customer_gstin: e.target.value.toUpperCase() }))} placeholder="22AAAAA0000A1Z5" maxLength={15} data-testid="inv-customer-gstin" />
            </div>
          </div>
        </div>

        {/* Item Table */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-5 space-y-4">
          <h3 className="text-sm font-semibold text-[#0F172A]">Line Items</h3>
          <div className="overflow-x-auto" style={{ overflow: 'visible' }}>
            <table className="w-full text-sm" data-testid="inv-items-table">
              <thead>
                <tr className="bg-[#F8FAFC] text-[#64748B] text-xs font-medium">
                  <th className="text-left p-2 w-[220px]">Item</th>
                  <th className="text-left p-2 w-[70px]">HSN</th>
                  <th className="text-center p-2 w-[65px]">Qty</th>
                  <th className="text-right p-2 min-w-[120px]">Rate</th>
                  <th className="text-right p-2 w-[130px]">Discount</th>
                  <th className="text-center p-2 min-w-[72px]">Tax%</th>
                  <th className="text-right p-2 w-[100px]">Amount</th>
                  <th className="text-left p-2 w-[180px]">Serial #s</th>
                  <th className="p-2 w-[36px]"></th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((li, idx) => {
                  const c = calcLine(li);
                  return (
                    <tr key={idx} className="border-t border-[#E2E8F0]" data-testid={`inv-line-item-${idx}`}>
                      <td className="p-2">
                        <ItemSearchSelect
                          items={catalogItems}
                          value={li.item_id}
                          displayName={li.name}
                          onSelect={(v) => selectCatalogItem(idx, v)}
                          testId={`inv-item-select-${idx}`}
                        />
                      </td>
                      <td className="p-2 text-xs text-[#64748B]">{li.hsn_sac || '-'}</td>
                      <td className="p-2">
                        <Input type="number" min="1" value={li.quantity} onChange={e => updateLineItem(idx, 'quantity', e.target.value)} className="text-center h-8 text-xs w-[60px]" data-testid={`inv-qty-${idx}`} />
                      </td>
                      <td className="p-2">
                        <Input type="number" value={li.rate} onChange={e => updateLineItem(idx, 'rate', e.target.value)} className="text-right h-8 text-xs" data-testid={`inv-rate-${idx}`} />
                      </td>
                      <td className="p-2">
                        <div className="flex items-center gap-1">
                          <Select value={li._discountType} onValueChange={v => updateLineItem(idx, '_discountType', v)}>
                            <SelectTrigger className="h-8 w-[48px] text-[10px] px-1"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="percent">%</SelectItem>
                              <SelectItem value="amount">Rs</SelectItem>
                            </SelectContent>
                          </Select>
                          {li._discountType === 'percent' ? (
                            <Input type="number" min="0" max="100" value={li.discount_percent} onChange={e => updateLineItem(idx, 'discount_percent', e.target.value)} className="text-right h-8 text-xs w-[60px]" data-testid={`inv-disc-${idx}`} />
                          ) : (
                            <Input type="number" min="0" value={li.discount_amount} onChange={e => updateLineItem(idx, 'discount_amount', e.target.value)} className="text-right h-8 text-xs w-[60px]" data-testid={`inv-disc-amt-${idx}`} />
                          )}
                        </div>
                      </td>
                      <td className="p-2">
                        <Input type="number" value={li.tax_percent} onChange={e => updateLineItem(idx, 'tax_percent', e.target.value)} className="text-center h-8 text-xs" data-testid={`inv-tax-${idx}`} />
                      </td>
                      <td className="p-2 text-right text-xs font-medium">{fmt(c.total)}</td>
                      <td className="p-2">
                        <div className="relative">
                          {li._availableSerials && li._availableSerials.length > 0 ? (
                            <>
                              <button
                                type="button"
                                onClick={() => updateLineItem(idx, '_showSerialDropdown', !li._showSerialDropdown)}
                                className="w-full h-8 text-xs border border-[#E2E8F0] rounded-md px-2 text-left bg-white hover:border-[#0F4C81] transition-colors flex items-center justify-between"
                                data-testid={`inv-serial-btn-${idx}`}
                              >
                                <span className={li.serial_numbers?.length ? 'text-[#0F172A]' : 'text-[#9CA3AF]'}>
                                  {li.serial_numbers?.length ? `${li.serial_numbers.length} selected` : 'Select S/N...'}
                                </span>
                                <svg width="10" height="6" viewBox="0 0 10 6" className={`transition-transform ${li._showSerialDropdown ? 'rotate-180' : ''}`}><path d="M1 1l4 4 4-4" stroke="#64748B" strokeWidth="1.5" fill="none" /></svg>
                              </button>
                              {li._showSerialDropdown && (
                                <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-[#E2E8F0] rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid={`inv-serial-dropdown-${idx}`}>
                                  {li._availableSerials.map(sn => {
                                    const checked = (li.serial_numbers || []).includes(sn);
                                    return (
                                      <label key={sn} className={`flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-[#F8FAFC] ${checked ? 'bg-[#EBF3FA]' : ''}`}>
                                        <input
                                          type="checkbox"
                                          checked={checked}
                                          onChange={() => toggleSerial(idx, sn)}
                                          className="rounded border-gray-300 text-[#0F4C81] focus:ring-[#0F4C81] h-3.5 w-3.5"
                                        />
                                        <span className="font-mono">{sn}</span>
                                      </label>
                                    );
                                  })}
                                </div>
                              )}
                              {li.serial_numbers?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {li.serial_numbers.map(sn => (
                                    <span key={sn} className="inline-flex items-center gap-0.5 text-[10px] bg-[#E8F5E9] text-[#0F4C81] px-1.5 py-0.5 rounded font-mono">
                                      {sn}
                                      <button type="button" onClick={() => toggleSerial(idx, sn)} className="text-[#64748B] hover:text-red-500 ml-0.5">&times;</button>
                                    </span>
                                  ))}
                                </div>
                              )}
                            </>
                          ) : (
                            <div className={`h-8 text-xs border border-dashed rounded-md px-2 flex items-center ${li._requiresSerial ? 'border-red-300 text-red-500' : 'border-[#E2E8F0] text-[#9CA3AF]'}`} data-testid={`inv-serial-${idx}`}>
                              {li.item_id ? (li._requiresSerial ? 'S/N required — no stock' : 'No stock serials') : 'Select item first'}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="p-2">
                        {lineItems.length > 1 && (
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => removeLineItem(idx)}>
                            <Trash size={14} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Button variant="outline" size="sm" onClick={addLineItem} data-testid="inv-add-item">
            <Plus size={14} className="mr-1" /> Add Item
          </Button>

          <div className="flex justify-end">
            <div className="w-64 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-[#64748B]">Subtotal</span><span>{fmt(totals.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-[#64748B]">Discount</span><span className="text-red-600">-{fmt(totals.discount)}</span></div>
              <div className="flex justify-between"><span className="text-[#64748B]">Tax</span><span>{fmt(totals.tax)}</span></div>
              <div className="flex justify-between font-semibold border-t border-[#E2E8F0] pt-1 text-base">
                <span>Grand Total</span><span className="text-[#0F4C81]" data-testid="inv-grand-total">{fmt(totals.grand)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Notes & T&C */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Customer Notes</label>
              <textarea value={form.customer_notes} onChange={e => setForm(f => ({ ...f, customer_notes: e.target.value }))}
                rows={3} className="w-full border border-[#E2E8F0] rounded-lg p-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#0F4C81]/20"
                placeholder="Notes for the customer..." data-testid="inv-notes" />
            </div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Terms & Conditions</label>
              <textarea value={form.terms_and_conditions} onChange={e => setForm(f => ({ ...f, terms_and_conditions: e.target.value }))}
                rows={3} className="w-full border border-[#E2E8F0] rounded-lg p-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#0F4C81]/20"
                placeholder="Terms and conditions..." data-testid="inv-terms" />
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-3 pb-8">
          <Button variant="outline" onClick={() => navigate('/invoices')}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-[#0F4C81] text-white hover:bg-[#0B3C68] px-8" data-testid="inv-submit-btn">
            <FloppyDisk size={16} className="mr-2" />{saving ? 'Creating...' : 'Create Invoice'}
          </Button>
        </div>
      </div>
    </div>
  );
}
